import type { ImportedVectorData } from './model';
import {
  hasSupportedGeoJsonExtension,
  importGeoJsonFile,
  type GeoJsonImportError,
} from './geojsonImport';
import type {
  PendingSelectiveShapefileImport,
  PendingShapefileImport,
  ShapefileImportError,
} from './shapefileImport';
import type { ImportedTableData, TabularImportError } from './tabularImport';
import type { XlsxWorkbookSession } from './xlsxImport';
import type { XlsxWorkerStage } from './xlsxWorkerProtocol';
import type { KmlImportError } from './kmlImport';
import type { GeoPackageSession } from './geopackageImport';

function hasSupportedKmlExtension(name: string): boolean {
  return /\.(?:kml|kmz)$/i.test(name);
}

function hasSupportedShapefileExtension(fileName: string): boolean {
  return /\.zip$/i.test(fileName);
}

export type DataImportResult =
  | { readonly status: 'imported'; readonly value: ImportedVectorData }
  | { readonly status: 'imported-many'; readonly values: readonly ImportedVectorData[] }
  | { readonly status: 'needs-tabular-intent'; readonly value: PendingTabularImport }
  | { readonly status: 'needs-crs'; readonly value: PendingShapefileImport }
  | { readonly status: 'needs-subset'; readonly value: PendingSelectiveShapefileImport }
  | { readonly status: 'needs-geopackage'; readonly value: GeoPackageSession }
  | {
      readonly status: 'error';
      readonly error:
        | TabularImportError
        | GeoJsonImportError
        | ShapefileImportError
        | KmlImportError
        | { readonly code: 'invalid-gpkg'; readonly message: string };
    };

export interface PendingTabularImport {
  readonly table: ImportedTableData;
  readonly workbook?: XlsxWorkbookSession;
}

export function isSupportedDataFileName(fileName: string): boolean {
  return (
    /\.csv$/i.test(fileName) ||
    /\.xlsx$/i.test(fileName) ||
    /\.gpkg$/i.test(fileName) ||
    hasSupportedGeoJsonExtension(fileName) ||
    hasSupportedKmlExtension(fileName) ||
    hasSupportedShapefileExtension(fileName)
  );
}

export interface DataImportOptions {
  readonly signal?: AbortSignal;
  readonly onXlsxStage?: (stage: XlsxWorkerStage) => void;
}

export async function importDataFile(
  file: File,
  options: DataImportOptions = {},
): Promise<DataImportResult> {
  if (/\.gpkg$/i.test(file.name)) {
    try {
      const { openGeoPackageFile } = await import('./geopackageImport');
      return { status: 'needs-geopackage', value: await openGeoPackageFile(file, options.signal) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          code: 'invalid-gpkg',
          message: error instanceof Error ? error.message : 'This GeoPackage could not be read.',
        },
      };
    }
  }
  if (/\.csv$/i.test(file.name)) {
    const { importCsvFile } = await import('./csvImport');
    const result = await importCsvFile(file);
    return result.ok
      ? { status: 'needs-tabular-intent', value: { table: result.value } }
      : { status: 'error', error: result.error };
  }
  if (/\.xlsx$/i.test(file.name)) {
    const { importXlsxFile } = await import('./xlsxImport');
    const result = await importXlsxFile(file, {
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.onXlsxStage ? { onStage: options.onXlsxStage } : {}),
    });
    return result.ok
      ? {
          status: 'needs-tabular-intent',
          value: { table: result.value.initialTable, workbook: result.value },
        }
      : { status: 'error', error: result.error };
  }
  if (hasSupportedGeoJsonExtension(file.name)) {
    const result = await importGeoJsonFile(file);
    return result.ok
      ? { status: 'imported', value: result.value }
      : { status: 'error', error: result.error };
  }
  if (hasSupportedKmlExtension(file.name)) {
    const { importKmlFile } = await import('./kmlImport');
    const result = await importKmlFile(file, options.signal);
    return result.ok
      ? { status: 'imported-many', values: result.values }
      : { status: 'error', error: result.error };
  }

  if (hasSupportedShapefileExtension(file.name)) {
    const { importShapefileZip } = await import('./shapefileImport');
    return importShapefileZip(file);
  }

  return {
    status: 'error',
    error: {
      code: 'unsupported-file-type',
      message:
        'Choose a CSV, XLSX, GeoJSON, GeoPackage, KML, KMZ, or a .zip containing one Shapefile dataset.',
    },
  };
}
